import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import { NumericFormatCustom } from '../../../components/NumericCustomFormatter';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  createBundleProduct,
  findBundleProduct,
  getBundleProduct,
  patchBundleProduct,
} from '../../../store/bundleProduct/api';
import { BundleProduct } from '../../../store/bundleProduct/api/interface';
import { decryptString } from '../../../utils/url';
import Loader from '../../../components/Loader';
import { toast } from 'react-toastify';
import { validateVariantGroups } from './variantValidation';
import { findSelectBundleCategory } from '../../../store/selectBundleCategory/api';
import { findSelectBundleSubcategory } from '../../../store/selectBundleSubcategory/api';
import { findSelectAddOnProduct } from '../../../store/selectAddOnProduct/api';
import { findSelectCategoryCake } from '../../../store/selectCategoryCake/api';
import { reset as resetSelectCategoryCake } from '../../../store/selectCategoryCake/selectCategoryCakeSlice';
import { findShape } from '../../../store/shape/api';
import { findSelectFlavorExport } from '../../../store/selectFlavorExport/api';
import { findSelectToppingsCategoryItemExport } from '../../../store/selectToppingCategoryItemExport/api';
import { CategoryCake } from '../../../store/categoryCake/api/interface';
import BundleImagesSection from './BundleImagesSection';
import BundleComponentsTab from './BundleComponentsTab';
import VariantsComponentsTab, {
  VariantsComponentsTabHandle,
} from './VariantsComponentsTab';
import BundleTabStatusBar from './BundleTabStatusBar';
import { BUNDLE_TAB_PANEL_BG } from './bundleFormTheme';
import Iconify from '../../../components/Iconify';
import {
  BundleComponentItem,
  BundleVariantGroup,
  createLocalId,
} from './types';
import {
  normalizeBundleModeStates,
  setBundleComponentsModeActive,
  setBundleVariantsModeActive,
  syncEmptyBundleStatusFields,
} from '../bundleProductStatus';
import { calculateCakeUnitPrice } from '../../../utils/calculateCakeUnitPrice';

const bundleProductSchema = Yup.object().shape({
  labelEn: Yup.string().required('English Label is required.'),
  labelAr: Yup.string().required('Arabic Label is required.'),
  bundleCategoryId: Yup.string().required('Bundle category is required.'),
  bundleSubcategoryId: Yup.string().required('Bundle subcategory is required.'),
  position: Yup.number().min(0).typeError('Must be Number'),
  effortScore: Yup.number()
    .integer()
    .min(1)
    .typeError('Must be a number')
    .required('Effort score is required.'),
});

const BundleProductForm = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const params: { id?: string } = useParams();
  const isAddMode = !params?.id;
  const [isPageLoading, setIsPageLoading] = useState(!isAddMode);
  const [bundleProductId, setBundleProductId] = useState('');
  const categories = useAppSelector((state) => state.selectBundleCategory.data);
  const subcategories = useAppSelector(
    (state) => state.selectBundleSubcategory.data
  );
  const addonProducts = useAppSelector((state) => state.selectAddOnProduct.data);
  const occasionCakes = useAppSelector(
    (state) => state.selectCategoryCake.data
  );
  const shapes = useAppSelector((state) => state.shape.data);
  const flavors = useAppSelector((state) => state.selectFlavorExport.data);
  const toppings = useAppSelector(
    (state) => state.selectToppingCategoryItemExport.data
  );
  const { accessToken } = useAppSelector((state) => state.auth);
  const assetUrl = useAppSelector(
    (state) => state.active.selectedItem.assetUrl
  );

  const [activeTab, setActiveTab] = useState(0);
  const [images, setImages] = useState<string[]>([]);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [components, setComponents] = useState<BundleComponentItem[]>([]);
  const [variantGroups, setVariantGroups] = useState<BundleVariantGroup[]>([]);
  const [componentsActiveState, setComponentsActiveState] = useState(true);
  const [variantsActiveState, setVariantsActiveState] = useState(false);
  const [overridePrice, setOverridePrice] = useState('');
  const [showVariantErrors, setShowVariantErrors] = useState(false);
  const variantsTabRef = useRef<VariantsComponentsTabHandle>(null);

  useEffect(() => {
    dispatch(findSelectBundleCategory({}));
    dispatch(findSelectAddOnProduct({}));
    dispatch(resetSelectCategoryCake());
    dispatch(findSelectCategoryCake({ limit: 100 }));
    // Shape/flavor/topping finds include price fields (category-cake nested includes may not on stage)
    dispatch(findShape({ limit: 100 }));
    dispatch(findSelectFlavorExport({ limit: 100 }));
    dispatch(findSelectToppingsCategoryItemExport({ limit: 100 }));
  }, [dispatch]);

  const pricedOccasionCakes = useMemo((): CategoryCake[] => {
    if (!occasionCakes.length) return occasionCakes;

    return occasionCakes.map((cake) => {
      const shape =
        shapes.find((item) => item.id === cake.shapeId) || cake.shape;
      const flavor =
        flavors.find((item) => item.id === cake.flavorId) || cake.flavor;
      const topping =
        toppings.find((item) => item.id === cake.toppingId) ||
        cake.topping_category_item;

      return {
        ...cake,
        shape,
        flavor,
        topping_category_item: topping,
      };
    });
  }, [occasionCakes, shapes, flavors, toppings]);

  // Recalculate saved cake component prices once priced occasion cakes load
  useEffect(() => {
    if (!pricedOccasionCakes.length) return;

    setComponents((prev) => {
      if (!prev.length) return prev;

      let changed = false;
      const next = prev.map((item) => {
        if (item.componentType !== 'cake') return item;

        const cake = pricedOccasionCakes.find((c) => c.id === item.productId);
        if (!cake?.shape || !cake?.flavor) return item;

        const unitPrice = calculateCakeUnitPrice(
          cake.shape,
          cake.flavor,
          cake.topping_category_item
        );

        if (unitPrice === 0 || unitPrice === item.unitPrice) return item;
        changed = true;
        return { ...item, unitPrice };
      });

      return changed ? next : prev;
    });
  }, [pricedOccasionCakes]);

  const {
    handleSubmit,
    values,
    handleChange,
    setFieldValue,
    setValues,
    isSubmitting,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      labelEn: '',
      labelAr: '',
      descriptionEn: '',
      descriptionAr: '',
      bundleCategoryId: '',
      bundleSubcategoryId: '',
      position: '0',
      effortScore: '10',
      activeState: true,
      showOnHomePage: false,
    },
    validationSchema: bundleProductSchema,
    onSubmit: async (formValues) => {
      try {
        const modeStates = normalizeBundleModeStates(
          componentsActiveState,
          variantsActiveState
        );

        let groupsForSave = variantGroups;
        if (modeStates.variantsActiveState && variantGroups.length > 0) {
          const prepared = variantsTabRef.current?.prepareForSave();
          if (prepared && !prepared.ok) {
            setShowVariantErrors(true);
            setActiveTab(1);
            toast.error(prepared.error);
            return;
          }
          if (prepared?.ok) {
            groupsForSave = prepared.groups;
            setVariantGroups(prepared.groups);
          }
        }

        const variantError =
          modeStates.variantsActiveState && groupsForSave.length > 0
            ? validateVariantGroups(groupsForSave)
            : null;
        if (variantError) {
          setShowVariantErrors(true);
          setActiveTab(1);
          toast.error(variantError);
          return;
        }

        setShowVariantErrors(false);
        const computedTotal = components.reduce(
          (sum, item) => sum + item.unitPrice,
          0
        );
        const usesVariants =
          modeStates.variantsActiveState && groupsForSave.length > 0;
        const isEmptyBundle = !components.length && !groupsForSave.length;

        const statusFields = isEmptyBundle
          ? syncEmptyBundleStatusFields(formValues.activeState)
          : {
              activeState: formValues.activeState,
              ...modeStates,
            };

        // Variants: default bundle price is 0 — option prices below do not affect it.
        // Components: sum of component unit prices.
        const basePrice = usesVariants ? 0 : computedTotal;

        const resolvedOverride =
          overridePrice !== '' ? Number(overridePrice) : null;

        const finalPrice =
          resolvedOverride != null && !Number.isNaN(resolvedOverride)
            ? resolvedOverride
            : basePrice;

        const payload = {
          labelEn: formValues.labelEn,
          labelAr: formValues.labelAr,
          descriptionEn: formValues.descriptionEn,
          descriptionAr: formValues.descriptionAr,
          bundleCategoryId: formValues.bundleCategoryId,
          bundleSubcategoryId: formValues.bundleSubcategoryId,
          position: formValues.position || 0,
          effortScore: Number(formValues.effortScore) || 10,
          price: finalPrice,
          discountedPrice: 0,
          previewUrl:
            (thumbnailUrl && images.includes(thumbnailUrl)
              ? thumbnailUrl
              : images[0]) || '',
          activeState: statusFields.activeState,
          showOnHomePage: formValues.showOnHomePage,
          overridePrice: resolvedOverride,
          componentsActiveState: statusFields.componentsActiveState,
          variantsActiveState: statusFields.variantsActiveState,
          images,
          components: components.map(
            ({ componentType, productId, labelEn, labelAr, unitPrice }) => ({
              componentType,
              productId,
              labelEn,
              labelAr,
              qty: 1,
              unitPrice,
            })
          ),
          variantGroups: groupsForSave.map(
            ({
              labelEn,
              labelAr,
              isRequired,
              selectionType,
              maxSelections,
              activeState,
              options,
            }) => ({
              labelEn,
              labelAr,
              isRequired,
              selectionType,
              maxSelections:
                selectionType === 'multiple'
                  ? Number(maxSelections) > 0
                    ? Number(maxSelections)
                    : Math.min(2, Math.max(options.length || 1, 1))
                  : 1,
              activeState,
              options: options.map(
                ({ labelEn, labelAr, previewUrl, price, productId }) => ({
                  labelEn,
                  labelAr,
                  previewUrl,
                  qty: 1,
                  price,
                  productId,
                })
              ),
            })
          ),
        };

        if (isAddMode) {
          await dispatch(createBundleProduct(payload as any)).unwrap();
        } else {
          await dispatch(
            patchBundleProduct({ id: bundleProductId, ...payload } as any)
          ).unwrap();
        }

        await dispatch(findBundleProduct({})).unwrap();
        navigation('/bundle-product', { state: { refreshedAt: Date.now() } });
      } catch (error) {
        console.error('Failed to save bundle product:', error);
      }
    },
  });

  const loadBundleProduct = (record: BundleProduct) => {
    setBundleProductId(record.id);

    const imageUrls =
      record.images?.map((image) => image.imageUrl).filter(Boolean) ||
      (record.previewUrl ? [record.previewUrl] : []);
    setImages(imageUrls);
    setThumbnailUrl(
      record.previewUrl && imageUrls.includes(record.previewUrl)
        ? record.previewUrl
        : imageUrls[0] || ''
    );

    setComponents(
      (record.components || []).map((component) => ({
        id: component.id || createLocalId(),
        componentType: component.componentType as BundleComponentItem['componentType'],
        productId: component.productId,
        labelEn: component.labelEn || '',
        labelAr: component.labelAr || '',
        qty: 1,
        unitPrice: Number(component.unitPrice) || 0,
      }))
    );

    const loadedVariantGroups = (record.variantGroups || []).map((group) => ({
      id: group.id || createLocalId(),
      labelEn: group.labelEn || '',
      labelAr: group.labelAr || '',
      isRequired: group.isRequired ?? false,
      selectionType: group.selectionType || 'single',
      maxSelections:
        group.selectionType === 'multiple'
          ? Number(group.maxSelections) > 0
            ? Number(group.maxSelections)
            : Math.min(2, Math.max((group.options || []).length || 1, 1))
          : 1,
      activeState: group.activeState ?? true,
      expanded: true,
      options: (group.options || []).map((option) => ({
        id: option.id || createLocalId(),
        productId: option.productId,
        labelEn: option.labelEn || '',
        labelAr: option.labelAr || '',
        previewUrl: option.previewUrl || '',
        qty: 1,
        price: Number(option.price) || 0,
      })),
    }));

    setVariantGroups(loadedVariantGroups);

    const loadedComponentsActive =
      record.componentsActiveState !== false &&
      String(record.componentsActiveState) !== 'false';
    const loadedVariantsActive =
      record.variantsActiveState !== false &&
      String(record.variantsActiveState) !== 'false';
    const modeStates = normalizeBundleModeStates(
      loadedComponentsActive,
      loadedVariantsActive
    );

    setActiveTab(
      modeStates.variantsActiveState && loadedVariantGroups.length > 0 ? 1 : 0
    );

    setValues({
      labelEn: record.labelEn || '',
      labelAr: record.labelAr || '',
      descriptionEn: record.descriptionEn || '',
      descriptionAr: record.descriptionAr || '',
      bundleCategoryId: record.bundleCategoryId || '',
      bundleSubcategoryId: record.bundleSubcategoryId || '',
      position: String(record.position ?? 0),
      effortScore: String(record.effortScore ?? 10),
      activeState:
        record.activeState !== false && String(record.activeState) !== 'false',
      showOnHomePage:
        record.showOnHomePage === true || String(record.showOnHomePage) === 'true',
    });

    setComponentsActiveState(modeStates.componentsActiveState);
    setVariantsActiveState(modeStates.variantsActiveState);
    setOverridePrice(
      record.overridePrice != null && record.overridePrice !== ''
        ? String(record.overridePrice)
        : ''
    );
  };

  useEffect(() => {
    const fetchBundleProduct = async () => {
      if (isAddMode || !params.id) return;

      try {
        setIsPageLoading(true);
        const record = await dispatch(
          getBundleProduct({ id: decryptString(params.id) })
        ).unwrap();
        loadBundleProduct(record);
      } catch (error) {
        navigation('/bundle-product');
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchBundleProduct();
  }, [dispatch, isAddMode, navigation, params.id, setValues]);

  useEffect(() => {
    if (values.bundleCategoryId) {
      dispatch(
        findSelectBundleSubcategory({
          bundleCategoryId: values.bundleCategoryId,
        })
      );
    }
  }, [values.bundleCategoryId, dispatch]);

  const computedTotal = useMemo(
    () =>
      components.reduce(
        (sum, item) => sum + item.unitPrice,
        0
      ),
    [components]
  );

  const usesComponentsMode = componentsActiveState;
  const usesVariantsMode = variantsActiveState;

  const finalPrice = useMemo(() => {
    if (overridePrice !== '') return Number(overridePrice) || 0;
    // Variants mode: always default to 0 (option prices do not affect bundle price)
    if (usesVariantsMode) return 0;
    return computedTotal;
  }, [overridePrice, computedTotal, usesVariantsMode]);

  const handleComponentsActiveStateChange = (value: boolean) => {
    const nextMode = value
      ? setBundleComponentsModeActive()
      : setBundleVariantsModeActive();
    setComponentsActiveState(nextMode.componentsActiveState);
    setVariantsActiveState(nextMode.variantsActiveState);
  };

  const handleVariantsActiveStateChange = (value: boolean) => {
    const nextMode = value
      ? setBundleVariantsModeActive()
      : setBundleComponentsModeActive();
    setComponentsActiveState(nextMode.componentsActiveState);
    setVariantsActiveState(nextMode.variantsActiveState);
  };

  const handleMainActiveStateChange = (value: boolean) => {
    setFieldValue('activeState', value);
  };

  const handleComponentsChange = (nextComponents: BundleComponentItem[]) => {
    setComponents(nextComponents);
  };

  const handleVariantGroupsChange = (nextGroups: BundleVariantGroup[]) => {
    setVariantGroups(nextGroups);
  };

  return (
    <Page title={isAddMode ? 'Bundle Product: Create' : 'Bundle Product: Edit'}>
      <Container
        maxWidth='xl'
        sx={{ minWidth: 0, overflowX: 'hidden' }}
      >
        <HeaderBreadcrumbs
          heading={isAddMode ? 'New Bundle Product' : 'Edit Bundle Product'}
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Bundle Products', href: '/bundle-product' },
            { name: isAddMode ? 'New' : 'Edit' },
          ]}
        />

        {isPageLoading ? (
          <Loader />
        ) : (
        <form onSubmit={handleSubmit}>
          <Card sx={{ p: 3, mb: 3, bgcolor: 'common.white' }}>
            <Typography
              variant='h6'
              sx={{ mb: 2 }}
            >
              General Information
            </Typography>

            <Grid
              container
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label='Label English'
                  name='labelEn'
                  placeholder='Bundle product name'
                  value={values.labelEn}
                  onChange={handleChange}
                  error={Boolean(errors.labelEn && touched.labelEn)}
                  helperText={touched.labelEn && errors.labelEn}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  label='Label Arabic'
                  name='labelAr'
                  placeholder='اسم المنتج'
                  value={values.labelAr}
                  onChange={handleChange}
                  error={Boolean(errors.labelAr && touched.labelAr)}
                  helperText={touched.labelAr && errors.labelAr}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <FormControl fullWidth>
                  <InputLabel>Select Category</InputLabel>
                  <Select
                    label='Select Category'
                    name='bundleCategoryId'
                    value={values.bundleCategoryId}
                    onChange={(e) => {
                      setFieldValue('bundleCategoryId', e.target.value);
                      setFieldValue('bundleSubcategoryId', '');
                    }}
                  >
                    {categories?.map((item) => (
                      <MenuItem
                        key={item.id}
                        value={item.id}
                      >
                        {item.labelEn}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
              >
                <FormControl fullWidth>
                  <InputLabel
                    shrink={
                      Boolean(values.bundleSubcategoryId) ||
                      (Boolean(values.bundleCategoryId) &&
                        (!subcategories || subcategories.length === 0)) ||
                      undefined
                    }
                  >
                    Select Sub Category
                  </InputLabel>
                  <Select
                    label='Select Sub Category'
                    name='bundleSubcategoryId'
                    value={values.bundleSubcategoryId}
                    onChange={handleChange}
                    displayEmpty
                    disabled={
                      Boolean(values.bundleCategoryId) &&
                      (!subcategories || subcategories.length === 0)
                    }
                    notched={
                      Boolean(values.bundleSubcategoryId) ||
                      (Boolean(values.bundleCategoryId) &&
                        (!subcategories || subcategories.length === 0)) ||
                      undefined
                    }
                    renderValue={(selected) => {
                      if (selected) {
                        const match = subcategories?.find(
                          (item) => item.id === selected
                        );
                        return match?.labelEn || selected;
                      }

                      if (
                        values.bundleCategoryId &&
                        (!subcategories || subcategories.length === 0)
                      ) {
                        return 'Not available';
                      }

                      return '';
                    }}
                  >
                    {!values.bundleCategoryId ? (
                      <MenuItem
                        disabled
                        value=''
                      >
                        Select category first
                      </MenuItem>
                    ) : !subcategories || subcategories.length === 0 ? (
                      <MenuItem
                        disabled
                        value=''
                      >
                        Not available
                      </MenuItem>
                    ) : (
                      subcategories.map((item) => (
                        <MenuItem
                          key={item.id}
                          value={item.id}
                        >
                          {item.labelEn}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label='Status'
                    value={values.activeState ? 'active' : 'inactive'}
                    onChange={(e) =>
                      handleMainActiveStateChange(e.target.value === 'active')
                    }
                  >
                    <MenuItem value='active'>Active</MenuItem>
                    <MenuItem value='inactive'>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label='English Description'
                  name='descriptionEn'
                  placeholder='Describe this bundle...'
                  value={values.descriptionEn}
                  onChange={handleChange}
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={6}
              >
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label='Arabic Description'
                  name='descriptionAr'
                  value={values.descriptionAr}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Effort Score'
                  name='effortScore'
                  value={values.effortScore}
                  onChange={handleChange}
                  InputProps={{ inputComponent: NumericFormatCustom as any }}
                  error={Boolean(errors.effortScore && touched.effortScore)}
                  helperText={
                    (Boolean(errors.effortScore && touched.effortScore) &&
                      errors.effortScore) ||
                    'Delivery slot capacity points for this bundle (default 10)'
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <FormControlLabel
                    sx={{
                      m: 0,
                      alignItems: 'center',
                      '& .MuiCheckbox-root': { p: 0.75 },
                      '& .MuiFormControlLabel-label': {
                        lineHeight: 1.25,
                      },
                    }}
                    control={
                      <Checkbox
                        color='primary'
                        name='showOnHomePage'
                        checked={values.showOnHomePage}
                        onChange={(e) =>
                          setFieldValue('showOnHomePage', e.target.checked)
                        }
                      />
                    }
                    label='Show on Home Page'
                  />
                </Box>
              </Grid>
            </Grid>

            <BundleImagesSection
              images={images}
              onChange={setImages}
              thumbnailUrl={thumbnailUrl}
              onThumbnailChange={setThumbnailUrl}
              accessToken={accessToken}
              assetUrl={assetUrl}
            />
          </Card>

          <Card
            sx={{
              p: 0,
              overflow: 'visible',
              bgcolor: 'common.white',
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 0,
            }}
          >
            <Box sx={{ bgcolor: BUNDLE_TAB_PANEL_BG, minWidth: 0 }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant='scrollable'
                scrollButtons='auto'
                allowScrollButtonsMobile
                sx={{
                  px: { xs: 1, sm: 2 },
                  bgcolor: 'common.white',
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 52,
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: 13, sm: 15 },
                    minHeight: 52,
                    color: 'text.secondary',
                    gap: 1,
                    minWidth: 'auto',
                    px: { xs: 1.5, sm: 2 },
                  },
                  '& .Mui-selected': {
                    color: 'primary.darker',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'primary.darker',
                    height: 3,
                  },
                }}
              >
                <Tab
                  icon={
                    <Iconify
                      icon='mdi:briefcase-outline'
                      width={20}
                      height={20}
                    />
                  }
                  iconPosition='start'
                  label='Bundle Components'
                />
                <Tab
                  icon={
                    <Iconify
                      icon='mdi:graph-outline'
                      width={20}
                      height={20}
                    />
                  }
                  iconPosition='start'
                  label='Variants Components'
                />
              </Tabs>

              <Box sx={{ px: { xs: 2, sm: 3 } }}>
                {activeTab === 0 && (
                  <BundleTabStatusBar
                    description='Use bundle components for this product. Activating this turns variants off.'
                    activeState={usesComponentsMode}
                    onActiveStateChange={handleComponentsActiveStateChange}
                  />
                )}
                {activeTab === 1 && (
                  <BundleTabStatusBar
                    description='Use variant options for this product. Activating this turns bundle components off.'
                    activeState={usesVariantsMode}
                    onActiveStateChange={handleVariantsActiveStateChange}
                  />
                )}
              </Box>
            </Box>

            <Box
              sx={{
                bgcolor: 'common.white',
                p: { xs: 2, sm: 3 },
                minWidth: 0,
                overflowX: 'hidden',
              }}
            >
              {activeTab === 0 && (
                <BundleComponentsTab
                  components={components}
                  onChange={handleComponentsChange}
                  addonProducts={addonProducts}
                  occasionCakes={pricedOccasionCakes}
                  computedTotal={computedTotal}
                  overridePrice={overridePrice}
                  onOverridePriceChange={setOverridePrice}
                  finalPrice={finalPrice}
                />
              )}

              {/* Keep mounted so max-selection draft validation works on save from Components tab */}
              <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
                <VariantsComponentsTab
                  ref={variantsTabRef}
                  variantGroups={variantGroups}
                  onChange={handleVariantGroupsChange}
                  accessToken={accessToken}
                  assetUrl={assetUrl}
                  showVariantErrors={showVariantErrors}
                  overridePrice={overridePrice}
                  onOverridePriceChange={setOverridePrice}
                  finalPrice={finalPrice}
                />
              </Box>
            </Box>
          </Card>

          <Stack
            direction='row'
            justifyContent='flex-end'
            spacing={2}
            sx={{ mt: 3 }}
          >
            <Button
              variant='outlined'
              onClick={() => navigation('/bundle-product')}
            >
              Cancel
            </Button>
            <LoadingButton
              type='submit'
              variant='contained'
              loading={isSubmitting}
            >
              {isAddMode ? 'Create Bundle Product' : 'Update Bundle Product'}
            </LoadingButton>
          </Stack>
        </form>
        )}
      </Container>
    </Page>
  );
};

export default BundleProductForm;
